import { Except } from '@common/types/common.type';
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import Button from '@ui/components/atoms/button/button.atom';
import Icon from '@ui/components/atoms/icon/icon.atom';
import { InputProps } from '@ui/components/molecules/input/input.molecule';
import Label, { LabelProps } from '@ui/components/molecules/label/label.molecule';
import List, {
  ListContainerProps,
  ListItemProps,
} from '@ui/components/molecules/list/list.molecule';
import withFormController, {
  WithFormControllerProps,
} from '@ui/hocs/with-form-controller/with-form-controller.hoc';
import useMergeRef from '@ui/hooks/use-merge-ref/use-merge-ref.hook';
import { SelectOption, SelectValue } from '@ui/types/select.type';
import { getSelectedOptionFromValue } from '@ui/utils/select.util';
import { clsxm } from '@ui/utils/style.util';
import useBoolean from '@util/hooks/use-boolean/use-boolean.hook';
import useControlState from '@util/hooks/use-control-state/use-control-state.hook';
import {
  ComponentProps,
  ComponentType,
  Fragment,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  SVGProps,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldValues } from 'react-hook-form';
import Search, { SearchProps } from '../search/search.organism';

// #region Types
type BaseItemProps = Except<ListItemProps, 'onSelect' | 'value'>;

/**
 * Props type for dropdown item component
 */
type ItemProps = Except<BaseItemProps, 'label'> & {
  isParent?: boolean;
  label?: ReactNode;
  /** Props to be passed to each menu item */
  menuItemProps?: ListItemProps;
  /** Whether multiple options can be selected */
  multiselect?: boolean;
  /** Callback when an option is selected */
  onSelect: (option: SelectOption, shouldClose?: boolean) => void;
  /** Callback to set the active element for focus management */
  onSetActiveElement: (ref: HTMLLIElement) => void;
  /** The option to render */
  option: SelectOption;
  /** Current selected value(s) */
  value: SelectValue | SelectValue[];
};

export type DropdownProps<FieldType extends FieldValues> = Pick<ItemProps, 'size'> &
  Except<WithFormControllerProps<FieldType>, 'onChange'> &
  Except<ComponentProps<'button'>, 'onChange'> &
  Pick<LabelProps<FieldType>, 'containerProps' | 'containerClassName' | 'label'> & {
    /** Custom display value override */
    displayValue?: string;
    helperText?: string;
    /** Icon to be displayed in the dropdown */
    IconComponent?: ComponentType<SVGProps<SVGSVGElement>>;
    /** Props to be passed to the input component */
    inputProps?: InputProps<FieldType>;
    isSearchable?: boolean;
    labelClassName?: string;
    labelProps?: Except<
      LabelProps<FieldType>,
      | 'htmlFor'
      | 'hasInput'
      | 'label'
      | 'containerProps'
      | 'containerClassName'
      | 'withFormErrorMessage'
      | 'formErrorControl'
      | 'formErrorName'
      | 'formErrorProps'
    >;
    menuItemClassName?: string;
    /** Props to be passed to each menu item */
    menuItemProps?: BaseItemProps;
    /** Props to be passed to the menu container */
    menuProps?: ListContainerProps;
    /** Whether multiple options can be selected */
    multiselect?: boolean;
    /** Callback when value changes */
    onChange?: (value: SelectValue | SelectValue[]) => void;
    /** Callback when selected options change */
    onSelectedOptionChange?: (selectedOptions: SelectOption[]) => void;
    /** Available options to select from */
    options?: SelectOption[];
    searchClassName?: string;
    searchProps?: SearchProps<FieldValues>;
    shouldResetValueWhenOptionsChange?: boolean;
    withErrorMessageHelper?: boolean;
  };
// #endregion

// #region Utils
function getLabel(option: SelectOption) {
  return option.label ?? (!Array.isArray(option.value) && option.value);
}
// #endregion

// #region Sub components
/**
 * Item component for rendering individual dropdown options
 * Features:
 * - Keyboard navigation
 * - Active state management
 * - Click and keyboard selection
 */
function Item({
  active: currentActive,
  isParent,
  label: passedLabel,
  menuItemProps,
  multiselect,
  onSelect,
  onSetActiveElement,
  option,
  trailing: currentTrailing,
  value,
  ...rest
}: ItemProps) {
  // #regionRefs
  const elementRef = useRef<HTMLLIElement>(null);
  // #endregion

  // #region Props
  const currentProps = useMemo(
    () => ({ ...rest, ...menuItemProps, ...option.props }),
    [menuItemProps, rest, option],
  );

  const active = useMemo(
    () =>
      !isParent &&
      (multiselect && !Array.isArray(option.value) && Array.isArray(value)
        ? value.includes(option.value)
        : value === option.value),
    [isParent, multiselect, option, value],
  );

  const label = useMemo(() => passedLabel ?? getLabel(option), [passedLabel, option]);
  // #endregion

  // #region Effects
  useEffect(() => {
    if (!active || !elementRef.current) return;

    onSetActiveElement(elementRef.current);
  }, [active, onSetActiveElement]);
  // #endregion

  // #region Handlers
  const handleOnClick: MouseEventHandler<HTMLLIElement> = useCallback(
    (event) => {
      if (isParent) return;

      onSelect(option);
      currentProps.onClick?.(event);
    },
    [currentProps, isParent, onSelect, option],
  );

  const handleOnKeyDown: KeyboardEventHandler<HTMLLIElement> = useCallback(
    (event) => {
      let sibling: HTMLLIElement | null = null;

      switch (event.code) {
        case 'Enter':
        case 'Space': {
          onSelect(option, true);
          break;
        }

        case 'ArrowRight':
        case 'ArrowDown': {
          sibling = elementRef.current?.nextSibling as HTMLLIElement;
          break;
        }

        case 'ArrowLeft':
        case 'ArrowUp': {
          sibling = elementRef.current?.previousSibling as HTMLLIElement;
          break;
        }

        default:
          break;
      }

      if (sibling) (sibling as HTMLLIElement)?.focus?.();

      currentProps.onKeyDown?.(event);
    },
    [currentProps, onSelect, option],
  );
  // #endregion

  return (
    <List.Item
      ref={elementRef}
      role="menuitem"
      tabIndex={0}
      {...currentProps}
      active={currentActive ?? active}
      className={clsxm(
        'rounded-none',
        isParent && 'bg-grey-border hover:bg-grey-border text-white cursor-default',
        currentProps.className,
      )}
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      size={currentProps.size}
      trailing={currentTrailing}
    >
      {label}
    </List.Item>
  );
}
// #endregion

/**
 * Dropdown component for selecting single or multiple options
 * Features:
 * - Single/Multi select support
 * - Keyboard navigation
 * - Form control integration
 * - Floating UI positioning
 * - Customizable styling
 */
function BaseDropdown<FieldType extends FieldValues>({
  className,
  containerClassName,
  containerProps,
  control,
  disabled,
  displayValue: currentDisplayValue,
  fieldState,
  formState,
  helperText: passedHelperText,
  IconComponent: PassedIconComponent,
  id: passedId,
  inputProps,
  isSearchable,
  label,
  labelClassName,
  labelProps,
  menuItemClassName,
  menuItemProps,
  menuProps,
  multiselect,
  onChange,
  onSelectedOptionChange,
  options,
  ref,
  searchClassName,
  searchProps,
  shouldResetValueWhenOptionsChange,
  size: passedSize,
  value: currentValue,
  withErrorMessageHelper,
  ...rest
}: DropdownProps<FieldType>) {
  // #region Refs and State
  const [open, { setIsOn, turnOff }] = useBoolean(false);
  const [shouldShow, setShouldShow] = useState(open);
  const activeElementRef = useRef<HTMLLIElement | null>(null);
  const [value, setValue] = useControlState<SelectValue | SelectValue[]>({
    controller: onChange,
    dependencies: [currentValue],
    initial: multiselect ? [] : '',
  });
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const [search, setSearch] = useState('');
  // #endregion

  // #region Handlers
  const handleOnSelect = useCallback(
    (option: SelectOption) => {
      if (Array.isArray(option.value)) return;

      setValue((current) => {
        if (Array.isArray(option.value)) return current;

        let newValue = current;
        if (Array.isArray(current)) {
          const filtered = current.filter((item) => item !== option.value);
          if (filtered.length < current.length) newValue = filtered;
          else newValue = [...current, option.value];
        } else newValue = option.value;

        if (options?.length) {
          onSelectedOptionChange?.(getSelectedOptionFromValue(newValue, options));

          if (!multiselect) turnOff();
        }

        return newValue;
      });
    },
    [setValue, options, multiselect, turnOff, onSelectedOptionChange],
  );
  const handleOnOpenChange = useCallback(
    (openParam: boolean) => {
      setIsOn(openParam);
      setSearch('');

      if (timeoutId.current) clearTimeout(timeoutId.current);

      timeoutId.current = setTimeout(
        () => {
          setShouldShow(openParam);
        },
        openParam ? 0 : 300,
      );
    },
    [setIsOn],
  );
  // #endregion

  // #region Floating UI Setup
  const { context, floatingStyles, middlewareData, refs } = useFloating({
    middleware: [
      flip({ padding: 8 }),
      shift(),
      offset({ mainAxis: 8 }),
      size({
        apply({ availableHeight, elements, rects }) {
          Object.assign(elements.floating.style, {
            maxHeight:
              availableHeight >= elements.floating.scrollHeight
                ? ''
                : `${Math.max(0, availableHeight)}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
      hide(),
    ],
    onOpenChange: handleOnOpenChange,
    open,
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss]);
  const handleRef = useMergeRef(ref, refs.setReference);
  // #endregion

  // #region Render Helpers
  const renderedOptions = useMemo(() => {
    const renderOption = (option: SelectOption, additionalKey?: unknown) => {
      const optionLabel = getLabel(option);
      if (search && !optionLabel?.toString().toLowerCase().includes(search.toLowerCase())) {
        return null;
      }

      return (
        <Item
          key={`${option.value}-${additionalKey}`}
          className={menuItemClassName}
          isParent={Array.isArray(option.value)}
          label={optionLabel}
          menuItemProps={menuItemProps}
          multiselect={multiselect}
          onSelect={(selectedOption, shouldClose) => {
            if (shouldClose) turnOff();
            handleOnSelect(selectedOption);
          }}
          onSetActiveElement={(element) => {
            activeElementRef.current = element;
          }}
          option={option}
          size={passedSize}
          value={value}
        />
      );
    };

    return options?.map((option, index) => (
      <Fragment key={`${option.value}-${index.toString()}`}>
        {renderOption(option)}
        {Array.isArray(option.value) &&
          option.value.map((subOption, subOptionIndex) =>
            renderOption(subOption, `${option.value}-${index}-${subOptionIndex}`),
          )}
      </Fragment>
    ));
  }, [
    handleOnSelect,
    menuItemClassName,
    menuItemProps,
    multiselect,
    options,
    passedSize,
    search,
    turnOff,
    value,
  ]);
  // #endregion

  // #region Computed Values
  const displayValue = useMemo(
    () =>
      getSelectedOptionFromValue(value, options ?? [])
        .map((item) => item.label?.toString())
        .join(', '),
    [value, options],
  );
  const IconComponent = useMemo(
    () => PassedIconComponent ?? Icon.FlipTriangle,
    [PassedIconComponent],
  );
  const { error: fieldError, invalid: isInvalid } = fieldState || {};
  const helperText = useMemo(
    () => passedHelperText ?? (withErrorMessageHelper && isInvalid ? fieldError?.message : ''),
    [passedHelperText, fieldError, isInvalid, withErrorMessageHelper],
  );
  const elementId = useId();
  const id = passedId ?? elementId;
  // #endregion

  // #region Effects
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (!shouldResetValueWhenOptionsChange) return;

    const selectedOption = getSelectedOptionFromValue(valueRef.current, options ?? []);
    if (selectedOption.length) return;

    setValue(multiselect ? [] : '');
  }, [shouldResetValueWhenOptionsChange, setValue, options, multiselect]);
  // #endregion

  return (
    <>
      <Label<FieldType>
        {...labelProps}
        className={clsxm('!mt-0', labelClassName, labelProps?.className)}
        containerClassName={clsxm('relative', containerClassName)}
        containerProps={containerProps}
        hasInput
        htmlFor={id}
        label={label}
      >
        <Label.FormErrorMessage<FieldType>
          className={isInvalid ? '' : 'text-inherit'}
          errorMessage={helperText}
          label={label}
        />
        <Button
          {...getReferenceProps({
            ...rest,
            className: clsxm(
              'w-full flex items-center justify-between space-x-2 text-inherit disabled:bg-grey-disabled disabled:border-grey-input-border',
              !label && (!isInvalid || !withErrorMessageHelper || !helperText) && '!mt-0',
              isInvalid && ['bg-red-light border-red-deep'],
              className,
            ),
            disabled,
            id,
            ref: handleRef,
          })}
        >
          <p data-testid="dropdown-display-value">{displayValue || '\xa0'}</p>
          <IconComponent
            className={clsxm(
              'shrink-0 transition-transform w-3 h-2.5',
              open ? 'rotate-180' : 'rotate-0',
            )}
          />
        </Button>
      </Label>
      {(open || shouldShow) && (
        <FloatingPortal>
          <FloatingFocusManager context={context} disabled={!open} initialFocus={activeElementRef}>
            <div
              {...getFloatingProps({
                className: clsxm(
                  'z-dropdown transition-opacity',
                  !!options?.length && shouldShow && open
                    ? 'opacity-100 pointer-events-auto'
                    : 'opacity-0 pointer-events-none',
                  middlewareData.hide?.referenceHidden && 'hidden',
                ),
                ref: refs.setFloating,
                style: floatingStyles,
              })}
              data-testid="dropdown-menu"
            >
              <List.Container
                {...menuProps}
                className={clsxm(
                  'overflow-y-auto p-0',
                  isSearchable ? 'max-h-[260px]' : 'max-h-[200px]',
                  menuProps?.className,
                )}
                role="menu"
              >
                {isSearchable && (
                  <List.Item className="sticky top-0 inset-x-0 hover:bg-white" contentAs="div">
                    <Search
                      onChange={setSearch}
                      value={search}
                      {...searchProps}
                      className={clsxm(
                        'font-normal',
                        menuItemClassName,
                        searchClassName,
                        searchProps?.className,
                      )}
                    />
                  </List.Item>
                )}
                {renderedOptions}
              </List.Container>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

const Dropdown = memo(withFormController(BaseDropdown));

export default Dropdown as typeof BaseDropdown;
